// react
import React, { useState, useEffect, useMemo } from "react";

// react-router-dom
import { useNavigate } from "react-router-dom";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  Add as AddIcon,
  DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";

// components
import { GroupRemoveConfirm } from "components/app/AppFeedback";

// features/databox
import getTagOwner from "features/databox/getTagOwner";
import { removeViewGroup, removeViewGroupReadOnly } from "features/databox/manageViewGroup";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const ViewGroupsTableToolbar = ({ openAddViewGroupDrawer, tagOwner }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1" fontWeight={700} color="text.secondary">
          {t("groups")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} container justifyContent="flex-end" spacing={1}>
        {tagOwner === user.uid && (
          <Grid item sm="auto">
            <Button
              onClick={openAddViewGroupDrawer}
              startIcon={<AddIcon />}
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {t("group")}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const ViewGroupsTableHead = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headCells = useMemo(
    () => [
      { id: "name", label: t("name") },
      { id: "timestamp", label: t("creation_datetime") },
      { id: "permissions", label: t("permissions") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"} sx={{ fontWeight: 700 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const ViewGroupsTable = ({ tag, groups, openAddViewGroupDrawer, handleOpenGroupRemovedSuccessful, handleOpenGroupRemovedError }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tagOwner, setTagOwner] = useState();
  const [selectedGroup, setSelectedGroup] = useState();
  const [showRemove, setShowRemove] = useState(false);

  useEffect(() => {
    const fetchTagOwner = async () => {
      const tagOwner = await getTagOwner(tag.id);
      setTagOwner(tagOwner);
    };
    fetchTagOwner();
  }, [tag]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShowRemove = (group) => {
    setShowRemove(true);
    setSelectedGroup(group);
  };

  const handleRemoveGroup = async () => {
    try {
      if (selectedGroup.permissions === "r/w") {
        await removeViewGroup(tag.id, selectedGroup.id, user.uid);
      } else if (selectedGroup.permissions === "r") {
        await removeViewGroupReadOnly(tag.id, selectedGroup.id, user.uid);
      }
      handleOpenGroupRemovedSuccessful();
    } catch (error) {
      console.error("Error in handleRemoveGroup:", error);
      handleOpenGroupRemovedError();
    } finally {
      setShowRemove(false);
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - groups.length) : 0;

  return (
    <>
      <Box>
        <ViewGroupsTableToolbar openAddViewGroupDrawer={openAddViewGroupDrawer} tagOwner={tagOwner} />

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
            backdropFilter: "blur(6px)",
            border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table data-testid="viewgroups-table">
            {!isMobile && <ViewGroupsTableHead />}

            <TableBody>
              {(rowsPerPage > 0 ? groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : groups).map((group) => {
                const name = group.name || "N/A";
                const created = group.added_on ? convertTimestamp(group.added_on) : "N/A";
                const permissionLabel = group.permissions === "r/w" ? "read/write" : "read";

                return isMobile ? (
                  <Box
                    key={group.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      mx: 1,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.01)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {created}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {permissionLabel}
                    </Typography>

                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <Tooltip title={t("remove")}>
                        <IconButton color="error" onClick={() => handleShowRemove(group)}>
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <TableRow
                    key={group.id}
                    sx={{
                      cursor: "pointer",
                      transition: "background 0.2s ease",
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}15`,
                        transform: "scale(1.002)",
                        transition: "all 0.2s ease",
                      },
                    }}
                  >
                    <TableCell onClick={() => navigate(`/${group.id}/group-settings`)}>{name}</TableCell>

                    <TableCell onClick={() => navigate(`/${group.id}/group-settings`)}>{created}</TableCell>

                    <TableCell onClick={() => navigate(`/${group.id}/group-settings`)}>{permissionLabel}</TableCell>

                    <TableCell align="right">
                      <Tooltip title={t("remove")} placement="top">
                        <IconButton color="error" onClick={() => handleShowRemove(group)}>
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!isMobile && emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={4} />
                </TableRow>
              )}
            </TableBody>

            {!isMobile && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20]}
                    count={groups.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("rows_per_page")}
                    labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>

          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <TablePaginationActions count={groups.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Box>
          )}
        </TableContainer>
      </Box>

      <GroupRemoveConfirm showRemove={showRemove} setShowRemove={setShowRemove} handleRemoveGroup={handleRemoveGroup} />
    </>
  );
};

ViewGroupsTable.propTypes = {
  tag: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
  openAddViewGroupDrawer: PropTypes.func.isRequired,
};

export default ViewGroupsTable;
